import React, { useEffect, useMemo } from "react";

import { useDispatch, useSelector } from "react-redux";
import { Button, ProgressIndicator, ErrorBanner, ReduxControls, Icon } from 'rhombus-react';
import { initialize, reduxForm, reset, Field, FieldArray } from "redux-form";

import { ADD_CREATIVES_TO_IMPOSIUM_PROJECT_FORM } from "widgets/forms/list";
import BasicModal from "widgets/modals/BasicModal";

import { addCreativesToImposiumProject } from "reducers/ImposiumProjects";
import { getImposiumCompositions } from "reducers/ImposiumCompositions";
import { loadCreatives } from 'reducers/Creatives';

import { creativeNameMaxLength, creativeNameAllowedChars, CREATIVE_NAME_MAX_LENGTH } from "widgets/forms/validators/Creatives";
import { creativeNameUnique } from 'widgets/modals/imposium/imposiumValidators';

import { requiredNotNull } from "widgets/forms/validators/validators";
import { formatAPIOption } from "widgets/social2/utils";


import "./AddCreativesToImposiumProjectModal.css";

function CreativeCompositionField(props){
	const { input, index } = props;
	const { name } = input;

	const compositions = useSelector(state => state.imposiumCompositions.items);

	const compositionOptions = useMemo(() => {
		return compositions?.map(formatAPIOption);
	}, [compositions]);

	const isFirstRow = index === 0;
	const rowClassName = `dimensions-row${isFirstRow ? "" : " dimensions-row--no-label"}`;

	return (
		<div key={name} className={rowClassName}>
			<div className="name-fields">
				<Field name={`${name}creativeName`}
					component={ReduxControls.Input}
					placeholder="Creative name"
					label={isFirstRow ? "Creative Name" : undefined}
					required={isFirstRow}
					validate={[requiredNotNull, creativeNameMaxLength, creativeNameAllowedChars, creativeNameUnique]}
					charMax={CREATIVE_NAME_MAX_LENGTH}
					showCharCount
				/>

				<Field name={`${name}composition`}
					component={ReduxControls.Select}
					type="select"
					placeholder="Select Composition..."
					label={isFirstRow ? "Select Composition" : undefined}
					required={isFirstRow}
					validate={[requiredNotNull]}
					isClearable={false}
					onMenuScrollToBottom={() => {}}
					options={compositionOptions}
				/>
			</div>
		</div>
	);
}

export function CreativeFieldArray(props){
	const { fields, errors } = props;
	const MAX_NEW_CREATIVES = 10;

	useEffect(() => {
		//start with one creative
		if (fields?.length === 0) {
			fields.push();
		}
	}, [fields]);

	const onAddCreative = () => {
		fields.push();
	};

	return (
		<ul className="creatives">
			{fields.map((key, index, fields) => {
				return (
					<li key={key} className="field">
						<Field name={key} component={CreativeCompositionField} formErrors={errors} index={index} />
						{fields.length > 1 && (<div><Icon className="remove-btn" name="far fa-times-circle" onClick={() => fields.remove(index)} /></div>)}
					</li>
				);
			})}
			{fields?.length < MAX_NEW_CREATIVES && (
				<li>
					<Button style="link" size="small" className="add-field" onClick={onAddCreative}>
						+ Add another creative
					</Button>
				</li>
			)}
		</ul>
	);
}

export function AddCreativesToImposiumProjectModal(props){
	const dispatch = useDispatch();
	const { handleSubmit, invalid, selectedProject, navigationActions, onClose } = props;

	const {
		libraryId,
		isSaving,
		isLoading,
		isLoadingCompositions,
		errorObj,
	} = useSelector((state) => {
		return {
			libraryId: state.libraryProperties.id,
			isSaving: state.imposiumProjects.isSaving,
			isLoading: state.imposiumProjects.isLoading,
			isLoadingCompositions: state.imposiumCompositions.isLoading,
			errorObj: state.imposiumProjects.error || {},
		};
	});

	function onBack() {
		dispatch(reset(ADD_CREATIVES_TO_IMPOSIUM_PROJECT_FORM));
		navigationActions?.popModal();
	}

	const submitCreatives = (handleSubmit((values) => {
		let postData = values?.creatives?.map((value) => {
			return {
				creativeName: value.creativeName,
				compositionId: value.composition.value,
			};
		});

		dispatch(addCreativesToImposiumProject(libraryId, selectedProject?.id, postData)).then((resp) => {
			if (resp.success) {
				onClose();
				return dispatch(loadCreatives(libraryId));
			}
		});
	}));

	useEffect(() => {
		dispatch(initialize(ADD_CREATIVES_TO_IMPOSIUM_PROJECT_FORM, {}));
		if (libraryId && selectedProject?.id) {
			dispatch(getImposiumCompositions(libraryId, selectedProject?.id, selectedProject?.name));
		}
	}, [libraryId, selectedProject]);

	function renderContent(){
		return (
			<div>
				<div className="add-creatives-help-text">Provide a creative name and select the composition to be used for the creative.</div>
				<div className="imposium-title"><strong>Project name:</strong> <span>{selectedProject?.name}</span></div>
				<form id={ADD_CREATIVES_TO_IMPOSIUM_PROJECT_FORM} onSubmit={submitCreatives} className="add-creatives-form">
					<ProgressIndicator key="loading" isLoading={isSaving || isLoading || isLoadingCompositions} />
					{ !!Object.keys(errorObj)?.length && <ErrorBanner
						title="Sorry, please fix the errors below and try again."
						messages={[errorObj.description]} />
					}
					<div>
						<FieldArray component={CreativeFieldArray} props={props} name="creatives" />
					</div>
				</form>
			</div>
		);
	}

	function renderFooter(){
		return ([
			<Button key="back" theme="default" onClick={onBack}>Back</Button>,
			<Button
				key="create"
				disabled={isSaving || isLoading || invalid}
				type="submit"
				theme="green"
				form={ADD_CREATIVES_TO_IMPOSIUM_PROJECT_FORM}>
				Create Dynamic Vast Creative
			</Button>
		]);
	}

	const modalProps = {
		title: `Create VAST - Dynamic - Imposium (Advanced Video)`,
		className: "create-imposium-project-modal add-creatives-to-imposium-project-modal",
		onClose,
		renderContent,
		renderFooter,
	};

	return <BasicModal {...modalProps} />;
}

AddCreativesToImposiumProjectModal.displayName = "AddCreativesToImposiumProjectModal";


export default reduxForm({
	form: ADD_CREATIVES_TO_IMPOSIUM_PROJECT_FORM,
})(AddCreativesToImposiumProjectModal);
